import { useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { CaretUpDown, CircleNotch, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/components/consumption/registerApi";

// Items are real SAP materials in their SAP unit - there is no parallel
// item master here, so the picker searches the known component master and
// falls back to a live SAP lookup when the unit is missing.
export const MaterialPicker = ({ onPick, busy, testIdPrefix = "setup-item" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  const onQuery = (value) => {
    setQuery(value);
    if (timer.current) clearTimeout(timer.current);
    if (!value.trim()) { setResults([]); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        setResults(await api.searchProducts(value.trim()));
      } catch (err) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={busy}
          className="h-9 w-full flex items-center justify-between px-3 rounded-md border border-[#D0D5DD] bg-white text-sm text-left disabled:opacity-50"
          data-testid={`${testIdPrefix}-search-trigger`}
        >
          <span className="text-[#98A2B3] flex items-center gap-2">
            {busy ? <CircleNotch size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />}
            {busy ? "Resolving the unit in SAP..." : "Search the material to map..."}
          </span>
          <CaretUpDown size={13} className="text-[#667085] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(420px,calc(100vw-32px))] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={onQuery} placeholder="Product ID or description..." data-testid={`${testIdPrefix}-search-input`} />
          <CommandList>
            {searching && <div className="py-3 text-center text-xs text-[#667085]">Searching...</div>}
            {!searching && query.trim() && results.length === 0 && <CommandEmpty>No matching material.</CommandEmpty>}
            <CommandGroup>
              {results.map((r) => (
                <CommandItem
                  key={r.product_id}
                  value={r.product_id}
                  onSelect={() => { onPick(r); setOpen(false); setQuery(""); setResults([]); }}
                  data-testid={`${testIdPrefix}-option-${r.product_id}`}
                >
                  <span className="font-mono text-xs font-bold text-[#101828] mr-2">{r.product_id}</span>
                  <span className="text-xs text-[#667085]">{r.description || "—"}{r.uom ? ` · ${r.uom}` : ""}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
